import { ChangeEvent, FocusEvent, memo, useEffect, useRef, useState } from "react";
import { Box, Modal, IconButton, MenuItem, Select } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import ChevronDown from "@zenith/assets/icons/ChevronDown";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import { ButtonComponent, TextFieldComponent } from "@zenith/components";
import { CheckBox } from "@zenith/components/ui/CheckBoxComponent/checkbox-component";
import {
  DEFAULT_COUNTRY_CODE,
  FLAG_ICON,
  PHONE_NUMBER_PREFIX,
} from "@zenith/data/constants";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { OutletListDTO } from "@zenith/services/user";
import {
  TeamManagementFields,
  TeamManagementForm,
  TeamManagementFormType,
  TeamManagementFormValidation,
} from "../../utils/form-fields";
import {
  FormHasError,
  INITIAL_DATA,
  INITIAL_ERROR_DATA,
  NO_ERROR,
  preventSpace,
  processValue,
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePhoneNumber,
} from "../../validations/form-field-validations";
import { ROLE_SELECT_ITEMS, TeamMemberRole } from "../../data/roles";
import SelectOutletsPanel from "../SelectOutletsPanel/select-outlets-panel";

export type TeamMemberDetails = TeamManagementFormType & {
  role: TeamMemberRole;
  outletIds: string[];
  createDefaultPassword: boolean;
};

export interface TeamMemberDetailsModalProps {
  showModal: boolean;
  role: TeamMemberRole;
  outlets: OutletListDTO[];
  /** From POST /invitations's 400/409 data.fields - phoneNumber/email came
   * back invalid or already in use. Cleared the moment the field is edited,
   * same as any other validation error here. */
  serverFieldErrors?: { phoneNumber?: string; email?: string };
  onRoleChange: (role: TeamMemberRole) => void;
  onViewBreakdown: () => void;
  onClose: () => void;
  onBack: () => void;
  onContinue: (details: TeamMemberDetails) => void;
}

const TeamMemberDetailsModal = memo(function TeamMemberDetailsModal(
  props: TeamMemberDetailsModalProps
) {
  const {
    showModal,
    role,
    outlets,
    serverFieldErrors,
    onRoleChange,
    onViewBreakdown,
    onClose,
    onBack,
    onContinue,
  } = props;

  const [formData, setFormData] =
      useState<TeamManagementFormType>(INITIAL_DATA),
    [formValidations, setFormValidations] =
      useState<TeamManagementFormValidation>(INITIAL_ERROR_DATA);
  const [outletIds, setOutletIds] = useState<string[]>([]);
  const [showOutletsPanel, setShowOutletsPanel] = useState(false);
  const [createDefaultPassword, setCreateDefaultPassword] = useState(false);

  // Admin is "not tied to an outlet" — all outlets apply by default and
  // the field isn't editable for this role. Switching the Role dropdown
  // away from Admin has to clear that back out, or outletIds keeps every
  // outlet id and the picker reads "All outlet (N)" for a role that was
  // never meant to default there.
  const isAdmin = role === TeamMemberRole.ADMIN;
  useEffect(() => {
    setOutletIds(isAdmin ? outlets.map((o) => o.id ?? "") : []);
  }, [isAdmin, outlets]);

  // This modal stays mounted, so its fields outlive the flow that opened it.
  // Opening again has to start from a blank form, or the member just added is
  // still sitting there as if it were new.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (showModal && !wasOpen.current) {
      setFormData(INITIAL_DATA);
      setFormValidations(INITIAL_ERROR_DATA);
      setShowOutletsPanel(false);
      setCreateDefaultPassword(false);
      setOutletIds(isAdmin ? outlets.map((outlet) => outlet.id ?? "") : []);
    }
    wasOpen.current = showModal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  // Server-side duplicate/invalid phone or email: merge into the same
  // formValidations state the client-side checks use, so it renders the
  // same way and onFieldChange's own reset-on-edit clears it like any
  // other error here.
  useEffect(() => {
    if (!serverFieldErrors?.phoneNumber && !serverFieldErrors?.email) return;
    setFormValidations((prev) => ({
      ...prev,
      ...(serverFieldErrors.phoneNumber
        ? {
            [TeamManagementFields.PHONE_NUMBER]: {
              message: serverFieldErrors.phoneNumber,
              hasError: true,
            },
          }
        : {}),
      ...(serverFieldErrors.email
        ? {
            [TeamManagementFields.EMAIL]: {
              message: serverFieldErrors.email,
              hasError: true,
            },
          }
        : {}),
    }));
  }, [serverFieldErrors]);

  function onFieldChange(
    field: TeamManagementFields
  ): (event: ChangeEvent<HTMLInputElement>) => void {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const newVal = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: processValue(field, newVal),
      }));
      setFormValidations((prev) => ({ ...prev, [field]: NO_ERROR }));
    };
  }

  function onFieldBlur(
    field: keyof TeamManagementForm,
    validate: (value: string) => { message: string; hasError: boolean }
  ): (_: FocusEvent<HTMLInputElement>) => void {
    return () => {
      setFormValidations((prev) => ({
        ...prev,
        [field]: validate(formData[field]),
      }));
    };
  }

  const resetState = () => {
    setFormData(INITIAL_DATA);
    setFormValidations(INITIAL_ERROR_DATA);
    setOutletIds([]);
    setCreateDefaultPassword(false);
    setShowOutletsPanel(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    resetState();
    onBack();
  };

  const handleContinue = () => {
    onContinue({ ...formData, role, outletIds, createDefaultPassword });
  };

  const outletsLabel = isAdmin
    ? `All outlet (${outlets.length})`
    : outletIds.length > 0
    ? outletIds.length === outlets.length
      ? `All outlet (${outlets.length})`
      : `${outletIds.length} outlet${outletIds.length > 1 ? "s" : ""} selected`
    : "Assign Outlets";

  // An admin covers every outlet and cannot pick any, so requiring a selection
  // locked the Continue button for that role permanently.
  const isFormValid =
    !FormHasError(formValidations, formData) &&
    (isAdmin || outletIds.length > 0);

  return (
    <Modal open={showModal} onClose={handleClose} className="overflow-visible">
      <Box className="absolute left-1/2 top-1/2 z-0 flex max-h-[90vh] w-[95%] max-w-[550px] -translate-x-1/2 -translate-y-1/2 transform flex-col overflow-y-auto rounded-xl bg-white p-8 outline-none">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-neutral-600"
          >
            <span>
              <ChevronLeft />
            </span>
            Back
          </button>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>

        <h2 className="mb-2 text-2xl font-semibold text-neutral-900">
          New Authorized User
        </h2>
        <p className="mb-6 text-sm text-neutral-600">
          Enter the information of the user you want to authorize.
        </p>

        <div className="mb-1 flex flex-col gap-1">
          <span
            id="team-member-role-label"
            className="text-sm text-neutral-700"
          >
            Role
          </span>
          <Select
            id="team-member-role"
            value={role}
            onChange={(event) =>
              onRoleChange(event.target.value as TeamMemberRole)
            }
            sx={{
              borderRadius: "10px",
              fontSize: 14,
              // The Select's root IS the InputBase root, so "& .MuiInputBase-root"
              // never matched and the control collapsed to its intrinsic height.
              height: 48,
              // MUI ships ".MuiSelect-select { height: auto }" at the same
              // specificity, so it wins on source order. Doubling the class
              // raises specificity instead of relying on !important.
              "& .MuiSelect-select.MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                height: "48px",
                minHeight: "48px",
                paddingTop: 0,
                paddingBottom: 0,
                // the sibling Joy text fields inset their text by 16px
                paddingLeft: "16px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#AFAFAE",
                borderRadius: "10px",
              },
            }}
          >
            {ROLE_SELECT_ITEMS.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  fontSize: 14,
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <button
          type="button"
          onClick={onViewBreakdown}
          className="mb-5 block w-fit self-start text-left text-xs font-medium text-[#5A5A59] underline"
        >
          View role access breakdown
        </button>

        <div className="mb-4 flex w-full gap-4">
          <div className="w-1/2">
            <TextFieldComponent
              id="team-member-first-name"
              label="First Name"
              labelId="team-member-first-name-label"
              placeholder="Enter first name"
              type="text"
              value={formData[TeamManagementFields.FIRST_NAME]}
              hasError={
                formValidations[TeamManagementFields.FIRST_NAME]?.hasError
              }
              onChange={onFieldChange(TeamManagementFields.FIRST_NAME)}
              onBlur={onFieldBlur(
                TeamManagementFields.FIRST_NAME,
                validateFirstName
              )}
            />
            {formValidations[TeamManagementFields.FIRST_NAME]?.hasError && (
              <div className="mt-1 ml-2 text-xs text-red-400">
                {formValidations[TeamManagementFields.FIRST_NAME].message}
              </div>
            )}
          </div>
          <div className="w-1/2">
            <TextFieldComponent
              id="team-member-last-name"
              label="Last Name"
              labelId="team-member-last-name-label"
              placeholder="Enter last name"
              type="text"
              value={formData[TeamManagementFields.LAST_NAME]}
              hasError={
                formValidations[TeamManagementFields.LAST_NAME]?.hasError
              }
              onChange={onFieldChange(TeamManagementFields.LAST_NAME)}
              onBlur={onFieldBlur(
                TeamManagementFields.LAST_NAME,
                validateLastName
              )}
            />
            {formValidations[TeamManagementFields.LAST_NAME]?.hasError && (
              <div className="mt-1 ml-2 text-xs text-red-400">
                {formValidations[TeamManagementFields.LAST_NAME].message}
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 flex w-full gap-1 md:gap-4">
          <div>
            <label className="label block whitespace-nowrap">
              Phone Number
            </label>
            <div className="text-field select-none bg-neutral-25">
              <div className="align-center flex justify-center p-3 text-neutral-400">
                <FLAG_ICON /> {PHONE_NUMBER_PREFIX}
              </div>
            </div>
          </div>
          <div className="grow pt-6">
            <TextFieldComponent
              id="team-member-phone-number"
              label=""
              labelId="team-member-phone-number-label"
              type="tel"
              maxLength={getPhoneNumberLength(
                DEFAULT_COUNTRY_CODE,
                formData.phoneNumber
              )}
              placeholder="Enter phone number"
              onChange={onFieldChange(TeamManagementFields.PHONE_NUMBER)}
              value={formData[TeamManagementFields.PHONE_NUMBER]}
              onBlur={onFieldBlur(
                TeamManagementFields.PHONE_NUMBER,
                validatePhoneNumber
              )}
              hasError={
                formValidations[TeamManagementFields.PHONE_NUMBER]?.hasError
              }
            />
            {formValidations[TeamManagementFields.PHONE_NUMBER]?.hasError && (
              <div className="mt-1 ml-2 text-xs text-red-400">
                {formValidations[TeamManagementFields.PHONE_NUMBER].message}
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 w-full">
          <TextFieldComponent
            id="team-member-email"
            label="Email Address"
            labelId="team-member-email-label"
            type="email"
            className="w-full"
            placeholder="Enter email address"
            onChange={onFieldChange(TeamManagementFields.EMAIL)}
            onKeyDown={preventSpace}
            value={formData[TeamManagementFields.EMAIL]}
            onBlur={onFieldBlur(TeamManagementFields.EMAIL, validateEmail)}
            hasError={formValidations[TeamManagementFields.EMAIL]?.hasError}
          />
          {formValidations[TeamManagementFields.EMAIL]?.hasError && (
            <div className="mt-1 ml-2 text-xs text-red-400">
              {formValidations[TeamManagementFields.EMAIL].message}
            </div>
          )}
        </div>

        <div className="relative mb-4 w-full">
          <label className="label mb-1.5 block text-xs font-medium text-neutral-600">
            Outlets
          </label>
          <button
            type="button"
            disabled={isAdmin}
            onClick={() => setShowOutletsPanel((v) => !v)}
            className={`flex w-full items-center justify-between rounded-lg border border-neutral-300 px-3 py-3 text-left text-sm ${
              isAdmin
                ? "cursor-not-allowed bg-neutral-100 text-neutral-500"
                : "text-neutral-900"
            } ${outletIds.length === 0 && !isAdmin ? "text-neutral-300" : ""}`}
          >
            {outletsLabel}
            <ChevronDown />
          </button>
          {showOutletsPanel && !isAdmin && (
            <SelectOutletsPanel
              outlets={outlets}
              selectedIds={outletIds}
              onChange={setOutletIds}
              onClose={() => setShowOutletsPanel(false)}
            />
          )}
        </div>

        <div className="mb-8">
          <CheckBox
            state={createDefaultPassword ? "checked" : "unchecked"}
            label="Create default password"
            onChange={(nextState) =>
              setCreateDefaultPassword(nextState === "checked")
            }
          />
        </div>

        <div className="flex w-full gap-4">
          <ButtonComponent
            id="team-member-details-cancel"
            type="secondary"
            size="large"
            label="Cancel"
            className="flex-1"
            onClick={handleClose}
          />
          <ButtonComponent
            id="team-member-details-continue"
            type="primary"
            size="large"
            label="Continue"
            className="flex-1"
            disabled={!isFormValid}
            onClick={handleContinue}
          />
        </div>
      </Box>
    </Modal>
  );
});

export default TeamMemberDetailsModal;
